import { CHATWOOT_BASE, authHeaders } from '@/api/chatwoot'
import { N8N_TEAMS_URL } from '@/api/n8n'
import type { LabelItem } from '@/types/chat'
import { getProtectedLabelTitles } from '@/composables/useProtectedLabels'
import { logCallConv } from '@/services/callConvService'

const ACCOUNT_PATH = `${CHATWOOT_BASE}/api/v1/accounts/3`

/** Muda o status da conversa (open/resolved/pending). Retorna o status resultante do servidor, ou null se falhar. */
export async function toggleConversationStatus(convId: number, token: string, status: string): Promise<string | null> {
  const res = await fetch(`${ACCOUNT_PATH}/conversations/${convId}/toggle_status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ status }),
  })
  if (!res.ok) return null
  const data = await res.json()
  return data.current_status ?? status
}

/** Silencia a conversa (usado hoje só pelo motor de macros). */
export async function muteConversation(convId: number, token: string): Promise<boolean> {
  const res = await fetch(`${ACCOUNT_PATH}/conversations/${convId}/mute`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  })
  return res.ok
}

/** Atribui (ou remove, com null) um agente à conversa. */
export async function assignAgent(convId: number, token: string, agentId: number | null): Promise<boolean> {
  const res = await fetch(`${ACCOUNT_PATH}/conversations/${convId}/assignments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ assignee_id: agentId }),
  })
  return res.ok
}

/** Atribui (ou remove, com null) um time à conversa. */
export async function assignTeam(convId: number, token: string, teamId: number | null): Promise<boolean> {
  const res = await fetch(`${ACCOUNT_PATH}/conversations/${convId}/assignments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ team_id: teamId }),
  })
  return res.ok
}

/**
 * Atribui (ou remove, com null) um agente à conversa via comando n8n
 * (`assign_conversation`, token de serviço) — usado tanto pro auto-atribuir
 * quando um agente responde uma conversa sem responsável (useMessageSender.ts)
 * quanto pela troca manual de agente em ConvDrawer/FloatChatWindow/FocoView/
 * MobileView. O token pessoal do agente pode não ter permissão de atribuição
 * em conversas fora do seu time/inbox no Chatwoot (mesmo problema de escopo
 * que já existia pra ler mensagens) — o token de serviço não tem essa
 * restrição.
 */
export async function assignAgentViaSystem(convId: number, agentId: number | null): Promise<boolean> {
  const res = await fetch(N8N_TEAMS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ command: 'assign_conversation', conversation_id: convId, assignee_id: agentId, timestamp: Date.now() }),
  })
  return res.ok
}

/**
 * Registra no histórico (tabela call_conv, migração v28) uma atribuição
 * MANUAL de agente — chamado pelos 4 pontos que trocam agente pelo menu
 * "agente" (ConvDrawer/FloatChatWindow/FocoView/MobileView), depois de
 * assignAgentViaSystem ter confirmado sucesso. Nunca chamado pra
 * desatribuir (agentId null) nem pelo auto-atribuir do useMessageSender.ts.
 * Silencioso em qualquer falha — é só log, não pode derrubar a atribuição
 * em si (que já aconteceu de verdade no Chatwoot nesse ponto).
 */
export async function logManualAssignment(
  convId: number,
  agentId: number,
  agentName: string,
  contactName: string,
  assignedById: number,
  assignedByName: string,
): Promise<void> {
  try {
    await logCallConv({
      conversationId: convId,
      agentId,
      agentName,
      contactName,
      isManual:       true,
      assignedById,
      assignedByName,
    })
  } catch (err) {
    // Não relança (log não pode quebrar o fluxo de atribuição), mas loga no
    // console pra não ficar 100% silencioso durante debug.
    console.error('[call_conv] falha ao registrar atribuição manual:', err)
  }
}

/**
 * Muda o status da conversa via comando n8n (token de serviço) — usado só pro
 * reabrir automático quando um agente responde uma conversa pendente
 * (useMessageSender.ts). Mesmo motivo do assignAgentViaSystem: o token pessoal
 * do agente pode não ter permissão fora do seu time/inbox no Chatwoot.
 */
export async function toggleConversationStatusViaSystem(convId: number, status: string): Promise<boolean> {
  const res = await fetch(N8N_TEAMS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ command: 'update_conversation_status', conversation_id: convId, status, timestamp: Date.now() }),
  })
  return res.ok
}

/** Busca a conversa completa (formato bruto da API do Chatwoot) por ID. Usado pelo motor de macros quando o chamador só tem o ID (ex: Run Drawer). */
export async function fetchConversation(convId: number, token: string): Promise<any | null> {
  const res = await fetch(`${ACCOUNT_PATH}/conversations/${convId}`, { headers: authHeaders(token) })
  if (!res.ok) return null
  return res.json()
}

/** Lista o catálogo de labels da conta (para o picker de etiquetas). */
export async function fetchAccountLabels(token: string): Promise<LabelItem[]> {
  const res = await fetch(`${ACCOUNT_PATH}/labels`, { headers: authHeaders(token) })
  if (!res.ok) return []
  const data = await res.json()
  return (data.payload || []).map((l: any) => ({ id: l.id, title: l.title, color: l.color }))
}

/** Lista as labels atualmente aplicadas a uma conversa (usado pela ação de macro remove_label). */
export async function fetchConversationLabels(convId: number, token: string): Promise<string[]> {
  const res = await fetch(`${ACCOUNT_PATH}/conversations/${convId}/labels`, { headers: authHeaders(token) })
  if (!res.ok) return []
  const data = await res.json()
  return data.payload || []
}

/**
 * Substitui a lista de labels da conversa. Retorna a lista resultante do servidor, ou null se falhar.
 *
 * Único ponto por onde toda alteração de etiquetas de conversa passa (toggle manual em
 * ConvDrawer/FloatChatWindow/MobileView e as ações de macro add_label/remove_label) —
 * por isso é aqui, e não em cada chamador, que uma etiqueta protegida (ver
 * useProtectedLabels.ts / migrations/admin-mysql-migration-v27.sql) é reinserida caso
 * o chamador tenha tentado removê-la. Custo zero quando nenhuma etiqueta está protegida.
 */
export async function setConversationLabels(convId: number, token: string, labels: string[]): Promise<string[] | null> {
  let finalLabels = labels
  const protectedTitles = getProtectedLabelTitles()
  if (protectedTitles.size > 0) {
    const current  = await fetchConversationLabels(convId, token)
    const mustKeep = current.filter(l => protectedTitles.has(l) && !labels.includes(l))
    if (mustKeep.length) finalLabels = [...labels, ...mustKeep]
  }

  const res = await fetch(`${ACCOUNT_PATH}/conversations/${convId}/labels`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ labels: finalLabels }),
  })
  if (!res.ok) return null
  const data = await res.json()
  return data.payload || finalLabels
}
